/**
 * station_seating.ts — who sits WHERE at an activity station (society wave,
 * 2026-09-19), pure.
 *
 * The server sends every versona doing a station activity to the station's one
 * authoritative point and never arbitrates seats (lib/activity_catalog.ts).
 * Seating is PRESENTATION: each client takes the versonas that have arrived in
 * a hosted state, sorts them by id, and deals them the station's formation
 * slots — so every client draws the same bench with the same people on it, and
 * no seat row ever crosses the wire.
 *
 *  · The LEADER of a gathering (the storyteller, the teacher, the performer,
 *    the healer) stands at the formation's focus, facing the group, and takes
 *    no slot.
 *  · Past `capacity` the rest stand in a loose ring behind — a crowd around a
 *    full table, never two bodies on one stool.
 *  · `seat` says what is UNDER them, which is what lets the clip grammar play a
 *    seated or lying clip honestly (the never-mime law).
 */
import { activityDef, stationDef, formationSlot, STATION_REACH_M } from './activity_catalog';
import type { SeatKind } from './versona_clips';

export interface StationRow { id: string; kind: string; x: number; z: number; facing: number; capacity: number }
export interface Person { key: string; state: string; x: number; z: number }
export interface Seat { x: number; z: number; yaw: number; seat: SeatKind; stationId: string; index: number; overflow: boolean; leader: boolean }

/** States that lead a gathering: they stand at the focus and face the group. */
export const LEADER_STATES: ReadonlySet<string> = new Set(['storytelling', 'teaching', 'performing', 'tending']);
/** Slack past the server's reach: a body eased by the speed governor is still "at" its station. */
export const SEAT_REACH_M = STATION_REACH_M + 3;
/** Where a healer stands relative to the cot they tend (station-local metres): beside it, half a step toward its foot. */
export const BEDSIDE_X_M = 0.85;
export const BEDSIDE_Z_M = 0.35;

/** What a station's prop puts under a versona in this state. */
export function seatKindOf(stationKind: string, state: string): SeatKind {
  const prop = stationDef(stationKind)?.prop ?? '';
  if (prop === 'cot') return state === 'convalescing' ? 'cot' : null;
  if (prop === 'desk') return LEADER_STATES.has(state) ? null : 'desk';   // the desk is drawn: `Sleep_on_Desk` may play
  if (prop === 'bench' || prop === 'table' || prop === 'fire_ring') return LEADER_STATES.has(state) ? null : 'chair';
  return null;
}

/** Numeric order of decimal-string ids without BigInt churn (shorter = smaller). */
function byId(a: string, b: string): number {
  return a.length !== b.length ? a.length - b.length : a < b ? -1 : a > b ? 1 : 0;
}

/** Station-local (lx, lz) → world, by three's rotation about Y (+z is the way the station faces). */
export function stationToWorld(st: { x: number; z: number; facing: number }, lx: number, lz: number): { x: number; z: number } {
  const c = Math.cos(st.facing), s = Math.sin(st.facing);
  return { x: st.x + lx * c + lz * s, z: st.z - lx * s + lz * c };
}

function fnv01(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; // finalizer: bare FNV is arithmetic in its last character
  return (h >>> 0) / 0xffffffff;
}

/** Where the leader of a gathering stands, station-local. */
function leaderSpot(formation: string, spacing: number): { x: number; z: number; face: number } {
  switch (formation) {
    case 'row': return { x: 0, z: spacing * 0.8, face: Math.PI };   // in front of the row, facing it (the healer at the cots)
    case 'grid': return { x: 0, z: spacing * 1.4, face: Math.PI };
    default: return { x: 0, z: 0, face: 0 };                       // the focus of an arc / circle / ring
  }
}

/**
 * Deal the seats. `people` are the versonas that have ARRIVED (the caller
 * filters walkers); anyone whose state no nearby station hosts gets no seat and
 * is drawn where the server put them.
 */
export function assignSeats(stations: readonly StationRow[], people: readonly Person[]): Map<string, Seat> {
  const out = new Map<string, Seat>();
  if (stations.length === 0 || people.length === 0) return out;
  const byStation = new Map<string, Person[]>();
  for (const p of people) {
    const act = activityDef(p.state);
    if (!act || act.stations.length === 0) continue;
    let best: StationRow | null = null; let bd = SEAT_REACH_M * SEAT_REACH_M;
    for (const st of stations) {
      if (!act.stations.includes(st.kind)) continue;
      const d = (st.x - p.x) ** 2 + (st.z - p.z) ** 2;
      if (d < bd) { bd = d; best = st; }
    }
    if (!best) continue;
    let list = byStation.get(best.id);
    if (!list) { list = []; byStation.set(best.id, list); }
    list.push(p);
  }
  for (const st of stations) {
    const list = byStation.get(st.id);
    if (!list) continue;
    const def = stationDef(st.kind);
    if (!def) continue;
    list.sort((a, b) => byId(a.key, b.key));
    const leaders = list.filter((p) => LEADER_STATES.has(p.state));
    const rest = list.filter((p) => !LEADER_STATES.has(p.state));
    // One leader holds the floor (the lowest id); any other stands with the group.
    const lead = leaders.shift();
    const group = [...rest, ...leaders].sort((a, b) => byId(a.key, b.key));
    const cap = Math.max(1, st.capacity);
    // THE SEAT-COUNT LAW (bug sweep, 2026-09-19): the formation is laid out for the station's
    // CAPACITY, because `station_props.ts` cuts its stools, cots, desks and kneelers from exactly
    // those slots and caches one merged geometry per kind. Dealing the arrivals over `group.length`
    // instead re-centred the row on however many had turned up, so a station's FIRST occupant — the
    // commonest case — sat beside the furniture: measured 0.90 m off a cot, 0.70 m off a desk,
    // 0.55 m off a tavern stool. A seated clip is only honest on the seat that is drawn.
    if (lead) {
      let l = leaderSpot(def.formation, def.spacing);
      // A PAIRED MOMENT: the healer stands at the BEDSIDE of the first patient (lowest id in a cot), a fixed
      // offset from that cot and facing it — two bodies on one sync point — not out in front of the whole row.
      if (lead.state === 'tending' && group.length > 0 && seatKindOf(st.kind, group[0].state) === 'cot') {
        const s0 = formationSlot(def.formation, 0, cap, def.spacing);
        l = { x: s0.x + BEDSIDE_X_M, z: s0.z + BEDSIDE_Z_M, face: Math.atan2(-BEDSIDE_X_M, -BEDSIDE_Z_M) };
      }
      const w = stationToWorld(st, l.x, l.z);
      out.set(lead.key, { x: w.x, z: w.z, yaw: st.facing + l.face, seat: null, stationId: st.id, index: -1, overflow: false, leader: true });
    }
    // The formation's reach, so the overflow ring stands clear of the WHOLE prop.
    let extent = def.spacing;
    for (let i = 0; i < cap; i++) { const s = formationSlot(def.formation, i, cap, def.spacing); extent = Math.max(extent, Math.hypot(s.x, s.z)); }
    for (let i = 0; i < group.length; i++) {
      const p = group[i];
      if (i < cap) {
        const s = formationSlot(def.formation, i, cap, def.spacing);
        const w = stationToWorld(st, s.x, s.z);
        out.set(p.key, { x: w.x, z: w.z, yaw: st.facing + s.face, seat: seatKindOf(st.kind, p.state), stationId: st.id, index: i, overflow: false, leader: false });
      } else {
        const a = fnv01(`${p.key}|${st.id}`) * Math.PI * 2;
        const r = extent + 1.6 + fnv01(`${st.id}|${p.key}`) * 1.2;
        const w = stationToWorld(st, Math.sin(a) * r, Math.cos(a) * r);
        out.set(p.key, { x: w.x, z: w.z, yaw: st.facing + a + Math.PI, seat: null, stationId: st.id, index: i, overflow: true, leader: false });
      }
    }
  }
  return out;
}
